import React, { useState, useCallback } from 'react';
import { SkillConfig } from '../../types/onboarding';
import { Button, Icons } from '../../components';
import { useCreateSkill, useUpdateSkill } from '../../apis/skill-registry';
import api from '../../../../lib/api';

import { useTranslation } from 'react-i18next';

interface MCPServerFormData {
  name: string;
  command: string;
  args?: string[];
  env?: Record<string, string>;
  cwd?: string;
  url?: string;
}

interface MCPTool {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

interface ServerValidationState {
  isValidating: boolean;
  isValidated: boolean;
  error?: string;
  tools?: MCPTool[];
  configHash?: string; // Hash of the server config for caching
}

interface McpSkillSetupProps {
  skill: SkillConfig & {
    isNewInstance?: boolean;
    instanceId?: string;
  };
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

// Validation API
async function validateMCPServer(server: MCPServerFormData): Promise<{
  tools: MCPTool[];
}> {
  const response = await api.post(
    '/api/core/pleri/skills/validate-mcp-server',
    {
      server,
    },
  );
  return response.data.data;
}

// Helper to generate a hash of server config
function generateConfigHash(server: MCPServerFormData): string {
  return btoa(JSON.stringify(server));
}

export const McpSkillSetup: React.FC<McpSkillSetupProps> = ({
  skill,
  onComplete,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  // Step tracking
  const [currentStep, setCurrentStep] = useState<'config' | 'details'>(
    'config',
  );

  // Server configuration state
  const [configText, setConfigText] = useState(
    JSON.stringify(
      {
        mcpServers: {},
      },
      null,
      2,
    ),
  );
  const [showJsonEditor, setShowJsonEditor] = useState(true);
  const [servers, setServers] = useState<Record<string, MCPServerFormData>>({});
  const [validationState, setValidationState] = useState<
    Record<string, ServerValidationState>
  >({});

  // Skill details state
  const [skillName, setSkillName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');

  // Form state
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API hooks
  const { mutateAsync: createSkill } = useCreateSkill();
  const { mutateAsync: updateSkill } = useUpdateSkill();

  const validateServer = useCallback(
    async (serverId: string, server: MCPServerFormData) => {
      const configHash = generateConfigHash(server);

      // Check if we have a cached validation with the same config hash
      if (
        validationState[serverId]?.configHash === configHash &&
        validationState[serverId]?.isValidated
      ) {
        return true;
      }

      setValidationState((prev) => ({
        ...prev,
        [serverId]: { isValidating: true, isValidated: false, configHash },
      }));

      try {
        const result = await validateMCPServer({
          ...server,
          name: serverId,
        });
        setValidationState((prev) => ({
          ...prev,
          [serverId]: {
            isValidating: false,
            isValidated: true,
            tools: result.tools,
            configHash,
          },
        }));
        return true;
      } catch (err) {
        setValidationState((prev) => ({
          ...prev,
          [serverId]: {
            isValidating: false,
            isValidated: false,
            error: err instanceof Error ? err.message : 'Validation failed',
            configHash,
          },
        }));
        return false;
      }
    },
    [validationState],
  );

  const handleConfigSubmit = async () => {
    try {
      // Parse and validate JSON
      const config = JSON.parse(configText);
      if (!config.mcpServers || typeof config.mcpServers !== 'object') {
        throw new Error(
          'Invalid configuration format. Must contain mcpServers object.',
        );
      }

      setServers(config.mcpServers);
      setShowJsonEditor(false);

      // Validate all servers
      await Promise.all(
        Object.entries(config.mcpServers).map(([serverId, server]) =>
          validateServer(serverId, server as MCPServerFormData),
        ),
      );

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON format');
    }
  };

  const handleRemoveServer = (serverId: string) => {
    setServers((prev) => {
      const newServers = { ...prev };
      delete newServers[serverId];
      return newServers;
    });
    setValidationState((prev) => {
      const newState = { ...prev };
      delete newState[serverId];
      return newState;
    });
  };

  const handleEditConfig = () => {
    setShowJsonEditor(true);
    setConfigText(JSON.stringify({ mcpServers: servers }, null, 2));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || isLoading) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      // Format servers to include their IDs as names
      const formattedServers = Object.entries(servers).reduce(
        (acc, [serverId, server]) => ({
          ...acc,
          [serverId]: {
            ...server,
            name: serverId,
          },
        }),
        {},
      );

      // Get all discovered tools from validation state
      const tools = Object.entries(validationState).reduce(
        (acc, [serverId, state]) => ({
          ...acc,
          [serverId]: state.tools || [],
        }),
        {},
      );

      // If this is an existing instance, use updateSkill
      if (skill.instanceId) {
        await updateSkill({
          skillType: skill.instanceId,
          enabled: true,
          config: {
            description,
            instructions,
            tools,
          },
          secrets: {
            mcpServers: formattedServers,
          },
        });
      } else {
        // For new instances, use createSkill
        await createSkill({
          skillType: skill.id.replace('-new', ''), // Remove -new suffix for new instances
          enabled: true,
          name: skillName,
          config: {
            description,
            instructions,
            tools,
          },
          secrets: {
            mcpServers: formattedServers,
          },
        });
      }

      await onComplete();
    } catch (error) {
      console.error('Failed to configure MCP skill:', error);
      setError(t('failedToConfigureMcpSkillPleaseCheck'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderConfigStep = () => (
    <div className="space-y-4">
      {showJsonEditor ? (
        <>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('mcpServerConfiguration')}
            </label>
            <textarea
              value={configText}
              onChange={(e) => setConfigText(e.target.value)}
              placeholder={t('mcpServersGithubCommandNpxArgsY')}
              className="block h-64 w-full rounded-md border border-gray-300 font-mono text-sm shadow-sm focus:border-[#FF732E] focus:ring-1 focus:ring-[#FF732E] focus:outline-none dark:border-gray-600"
            />
          </div>
          <Button onClick={handleConfigSubmit} className="w-full">
            {t('addServers')}
          </Button>
        </>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {t('mcpServers')}
            </h3>
            <Button onClick={handleEditConfig} className="text-sm">
              {t('editConfiguration')}
            </Button>
          </div>

          {Object.entries(servers).map(([serverId, server]) => {
            return (
              <div
                key={serverId}
                className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        {serverId}
                      </h4>
                      {validationState[serverId]?.isValidating && (
                        <Icons.Spinner className="h-4 w-4 text-[#FF732E]" />
                      )}
                      {validationState[serverId]?.isValidated && (
                        <Icons.Check className="h-4 w-4 text-green-500" />
                      )}
                      {validationState[serverId]?.error && (
                        <Icons.Warning className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {server.url ? (
                        <>{t('mcpUrl', { url: server.url })}</>
                      ) : (
                        <>
                          {server.command} {server.args?.join(' ')}
                        </>
                      )}
                    </p>
                    {Object.entries(server.env || {}).map(([key]) => (
                      <div key={key} className="flex items-center gap-1">
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                          {key}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleRemoveServer(serverId)}
                      className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-400"
                    >
                      <Icons.Plus className="h-4 w-4 rotate-45" />
                    </button>
                  </div>
                </div>
                {validationState[serverId]?.error && (
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-sm text-red-600">
                      {validationState[serverId].error}
                    </div>
                    <button
                      onClick={() => validateServer(serverId, server)}
                      className="ml-2 inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      <Icons.Refresh className="h-3 w-3" />
                      {t('retryValidation')}
                    </button>
                  </div>
                )}
                {validationState[serverId]?.tools && (
                  <div className="mt-4">
                    <div className="mb-2">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('tools')}
                      </h5>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {validationState[serverId].tools.map((tool) => (
                        <span
                          key={tool.name}
                          className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                        >
                          {tool.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {Object.keys(servers).length > 0 &&
            Object.values(validationState).every(
              (state) => state.isValidated,
            ) && (
              <Button
                onClick={() => setCurrentStep('details')}
                className="w-full"
              >
                {t('continueToSetup')}
              </Button>
            )}
        </div>
      )}
    </div>
  );

  const renderDetailsStep = () => (
    <div className="space-y-6">
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('skillName')}
        </label>
        <input
          type="text"
          value={skillName}
          onChange={(e) => setSkillName(e.target.value)}
          placeholder={t('eGGithubTools')}
          required
          className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#FF732E] focus:ring-1 focus:ring-[#FF732E] focus:outline-none dark:border-gray-600"
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('common:description')}
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('describeWhatThisMcpSkillDoes')}
          required
          className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#FF732E] focus:ring-1 focus:ring-[#FF732E] focus:outline-none dark:border-gray-600"
          rows={2}
        />
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('instructions')}
        </label>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder={t('enterTheInstructionsForThisSkill')}
          required
          className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#FF732E] focus:ring-1 focus:ring-[#FF732E] focus:outline-none dark:border-gray-600"
          rows={3}
        />
      </div>

      <div className="flex gap-3">
        <Button
          variant="secondary"
          onClick={() => setCurrentStep('config')}
          className="flex-1"
        >
          {t('backToServers')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex-1"
        >
          {isSubmitting ? t('creatingSkill') : t('createMcpSkill')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
          {t('setUpMcpSkill')}
        </h2>
      </div>
      {/* 3. Short Description */}
      <div className="mb-8 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          {currentStep === 'config' &&
            (showJsonEditor
              ? t('enterYourMcpServerConfiguration')
              : t('configureMcpServers'))}
          {currentStep === 'details' && t('configureYourMcpSkill')}
        </p>
      </div>
      {/* Error Message */}
      {error && (
        <div className="mb-8 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950">
          <div className="flex items-center gap-2">
            <Icons.Warning className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        </div>
      )}
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 5. Form Fields */}
        <div className="mb-8">
          {currentStep === 'config' && renderConfigStep()}
          {currentStep === 'details' && renderDetailsStep()}
        </div>
      </div>
    </div>
  );
};
